import { useCallback, useEffect, useState } from "react";
import type { BusinessProfileReadinessV1 } from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../../api.js";

function readinessLabel(level: BusinessProfileReadinessV1["readinessLevel"]): string {
  return level
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function V10RichnessPanel({
  api,
  projectId,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string | null;
}) {
  const [readiness, setReadiness] = useState<BusinessProfileReadinessV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const evaluate = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    setError(null);
    try {
      const active = await api.getActiveBusinessGraph(projectId);
      const result = await api.evaluateBusinessProfileReadiness(projectId, active.businessGraph);
      setReadiness(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setReadiness(null);
    } finally {
      setBusy(false);
    }
  }, [api, projectId]);

  useEffect(() => {
    if (!projectId) {
      setReadiness(null);
      setError(null);
      return;
    }
    void evaluate();
  }, [evaluate, projectId]);

  if (!projectId) return null;

  return (
    <div className="v10-richness-panel">
      <p className="v10-cl-kicker">Package eligibility</p>
      {busy && !readiness ? <p className="v10-muted">Evaluating readiness…</p> : null}
      {error ? <p className="v10-validation">{error}</p> : null}
      {readiness ? (
        <>
          <p>
            Readiness: <strong>{readinessLabel(readiness.readinessLevel)}</strong>
          </p>
          {readiness.blockedPackages.length > 0 ? (
            <p className="v10-validation">Blocked packages: {readiness.blockedPackages.join(", ")}</p>
          ) : (
            <p className="v10-muted">All requested packages eligible for this profile.</p>
          )}
          {readiness.availablePackages.length > 0 ? (
            <p className="v10-muted">Available: {readiness.availablePackages.join(", ")}</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
